/**
 * Transform functions for Critical Illness benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields with separate type and amount fields)
 * - Backend API format (nested objects with type and amount fields)
 */

import {
  RfpAgeBasisEnum,
  RfpInclusionStatusEnum,
} from "@platform/openapi-types";

import {
  cleanupNotDisclosedBenefits,
  shouldIncludeBenefitInApi,
  transformAgeReductionScheduleFromApi,
  transformAgeReductionScheduleToApi,
  transformBenefitPersonFromApi,
  transformBenefitPersonToApi,
  transformPreExistingConditionFromApi,
  transformPreExistingConditionToApi,
  transformTypeAmountFromApi,
  transformTypeAmountToApi,
} from "../shared/builders";

import type {
  CriticalIllnessBenefitsApi,
  CriticalIllnessBenefitsGrid,
  CriticalIllnessChildBenefit,
  CriticalIllnessCoverage,
  CriticalIllnessEmployeeBenefit,
  CriticalIllnessMaximumPayout,
  CriticalIllnessPreExistingCondition,
  CriticalIllnessSpouseBenefit,
  CriticalIllnessWellnessBenefit,
} from "./types";

interface ChildAmountOption {
  amount: number;
}

const isChildAmountOption = (value: unknown): value is ChildAmountOption =>
  typeof value === "object" &&
  value !== null &&
  "amount" in value &&
  typeof (value as ChildAmountOption).amount === "number" &&
  (value as ChildAmountOption).amount > 0;

const isValidNonNegative = (value: unknown): value is number =>
  typeof value === "number" && value >= 0;

interface ChildBenefitDescription {
  type: string;
  amounts?: number[];
  minimum?: number;
}

const buildChildBenefitDescription = (
  childType: string,
  childAmount: unknown,
  childMinimum: unknown,
): ChildBenefitDescription => {
  const description: ChildBenefitDescription = { type: childType };

  if (childType === "flatAmount") {
    if (Array.isArray(childAmount)) {
      const amounts = childAmount
        .filter(isChildAmountOption)
        .map((o) => o.amount);
      if (amounts.length > 0) {
        description.amounts = amounts;
      }
    } else if (isValidNonNegative(childAmount)) {
      description.amounts = [childAmount];
    }

    if (isValidNonNegative(childMinimum)) {
      description.minimum = childMinimum;
    }
  } else if (childType === "percentageOfEeBenefit") {
    description.amounts = [isValidNonNegative(childAmount) ? childAmount : 0];

    if (isValidNonNegative(childMinimum)) {
      description.minimum = childMinimum;
    }
  }

  return description;
};

interface Occurrence {
  type: string;
  months?: number;
}

interface OccurrencesApi {
  occurrenceOfDifferentIllness?: Occurrence;
  additionalOccurrenceOfSameIllness?: Occurrence;
}

const validateOccurrenceMonths = (
  months: unknown,
  fieldName: string,
): number => {
  if (!shouldIncludeBenefitInApi(months)) {
    throw new Error(
      `${fieldName} type is "months" but months value is missing`,
    );
  }

  const value = Number(months);
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(
      `${fieldName} months must be a non-negative number, got: ${String(months)}`,
    );
  }

  return value;
};

const buildIllnessOccurrence = (
  type: unknown,
  months: unknown,
  fieldName: string,
): Occurrence | undefined => {
  if (!shouldIncludeBenefitInApi(type)) {
    return undefined;
  }

  const occurrence: Occurrence = { type: type as string };
  if (type === "months") {
    occurrence.months = validateOccurrenceMonths(months, fieldName);
  }

  return occurrence;
};

const transformOccurrencesToApi = (
  diffType: unknown,
  diffMonths: unknown,
  sameType: unknown,
  sameMonths: unknown,
): OccurrencesApi => {
  const result: OccurrencesApi = {};

  const diff = buildIllnessOccurrence(
    diffType,
    diffMonths,
    "occurrenceOfDifferentIllness",
  );
  if (diff) {
    result.occurrenceOfDifferentIllness = diff;
  }

  const same = buildIllnessOccurrence(
    sameType,
    sameMonths,
    "additionalOccurrenceOfSameIllness",
  );
  if (same) {
    result.additionalOccurrenceOfSameIllness = same;
  }

  return result;
};

interface ApiChildBenefitDescription {
  type?: string;
  amounts?: number[];
  minimum?: number;
}

interface ChildBenefitGridFields {
  childBenefitDescriptionType: string;
  childBenefitDescriptionAmount?: number | { amount: number }[];
  childBenefitDescriptionMinimum?: number;
}

const transformChildBenefitsFromApi = (
  child: { benefitDescription?: ApiChildBenefitDescription } | undefined,
): Partial<ChildBenefitGridFields> => {
  if (!child?.benefitDescription) {
    return {};
  }

  const desc = child.benefitDescription;
  const descType = desc.type || "notIncluded";

  const result: ChildBenefitGridFields = {
    childBenefitDescriptionType: descType,
  };

  if (descType === "flatAmount" && desc.amounts) {
    result.childBenefitDescriptionAmount =
      desc.amounts.length > 0
        ? desc.amounts
            .filter((v) => typeof v === "number" && v > 0)
            .map((v) => ({ amount: v }))
        : desc.amounts[0];
    result.childBenefitDescriptionMinimum = desc.minimum;
  } else if (descType === "percentageOfEeBenefit") {
    result.childBenefitDescriptionAmount = desc.amounts?.[0];
    result.childBenefitDescriptionMinimum = desc.minimum;
  } else {
    result.childBenefitDescriptionAmount = undefined;
    result.childBenefitDescriptionMinimum = undefined;
  }

  return result;
};

interface ApiOccurrence {
  type?: string;
  months?: number;
}

interface OccurrenceGridFields {
  [key: string]: string | number | undefined;
}

const transformOccurrenceFromApi = (
  occurrence: ApiOccurrence | undefined,
  prefix: string,
): OccurrenceGridFields => {
  if (!occurrence) {
    return {};
  }

  const result: OccurrenceGridFields = {
    [`${prefix}Type`]: occurrence.type,
  };

  if (occurrence.type === "months") {
    result[`${prefix}Months`] = occurrence.months;
  }

  return result;
};

interface ApiDependentHolder {
  spouse?: { benefitDescription?: { type?: string } };
  child?: { benefitDescription?: { type?: string } };
}

const deriveDependentBenefitsIncluded = (
  benefits: ApiDependentHolder,
): "yes" | "no" | undefined => {
  // No dependent object at all means the analyst never answered the question —
  // distinct from spouse/child data that says notIncluded. Answering "no" here
  // would show a value nobody entered and, because the toggle then reads as
  // answered, let the to-API clearing stamps persist on the next save.
  if (!benefits.spouse && !benefits.child) {
    return undefined;
  }

  const hasSpouse =
    !!benefits.spouse?.benefitDescription?.type &&
    benefits.spouse.benefitDescription.type !== "notIncluded";
  const hasChild =
    !!benefits.child?.benefitDescription?.type &&
    benefits.child.benefitDescription.type !== "notIncluded";

  return hasSpouse || hasChild ? "yes" : "no";
};

/**
 * Transforms Critical Illness benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures for employee, spouse, child,
 * illness-specific coverages, payout limits, and occurrence tracking.
 *
 * Note: dependentBenefitsIncluded is tri-state. "yes" builds spouse and child from
 * the grid; "no" writes notIncluded to clear whatever was saved before; unset ("",
 * undefined) omits both, because the analyst never answered the question and there
 * is nothing to clear. A newly materialized column holds unset, so stamping it
 * would persist dependent values nobody entered.
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformCriticalIllnessBenefitsToApi = (
  benefits: CriticalIllnessBenefitsGrid,
): CriticalIllnessBenefitsApi => {
  const transformed: Partial<CriticalIllnessBenefitsApi> = {
    objectType: "criticalIllness",
  };

  // Check if dependent benefits should be included
  const includeDependents = benefits.dependentBenefitsIncluded === "yes";
  const dependentsAnswered = shouldIncludeBenefitInApi(
    benefits.dependentBenefitsIncluded,
  );

  // ========================================
  // EMPLOYEE BENEFITS
  // ========================================

  const employee = transformBenefitPersonToApi(
    benefits.employeeBenefitDescriptionType,
    benefits.employeeBenefitDescriptionAmount,
    benefits.employeeBenefitDescriptionMinimum,
    benefits.employeeBenefitMaximumType,
    benefits.employeeBenefitMaximumDollarAmount,
    benefits.employeeBenefitMaximumPercent,
    benefits.employeeGuaranteeIssueType,
    benefits.employeeGuaranteeIssueAmount,
  );

  if (Object.keys(employee).length > 0) {
    transformed.employee = employee as CriticalIllnessEmployeeBenefit;
  }

  // ========================================
  // SPOUSE BENEFITS (conditionally included)
  // ========================================

  if (includeDependents) {
    const spouse = transformBenefitPersonToApi(
      benefits.spouseBenefitDescriptionType,
      benefits.spouseBenefitDescriptionAmount,
      benefits.spouseBenefitDescriptionMinimum,
      benefits.spouseBenefitMaximumType,
      benefits.spouseBenefitMaximumDollarAmount,
      benefits.spouseBenefitMaximumPercent,
      benefits.spouseGuaranteeIssueType,
      benefits.spouseGuaranteeIssueAmount,
    );

    if (Object.keys(spouse).length > 0) {
      transformed.spouse = spouse as CriticalIllnessSpouseBenefit;
    }
  } else if (dependentsAnswered) {
    // Explicitly clear spouse benefits so the PATCH overwrites any previously saved data
    transformed.spouse = {
      benefitDescription: { type: "notIncluded" },
      benefitMaximum: { type: "notIncluded" },
      guaranteeIssue: { type: "notIncluded" },
    } as CriticalIllnessSpouseBenefit;
  }

  // ========================================
  // CHILD BENEFITS (conditionally included)
  // ========================================

  if (includeDependents) {
    // Mirror spouse's guard above: only send child when the analyst actually
    // answered its type field. Defaulting to "notIncluded" whenever it was
    // merely unanswered (as this used to) stamped an explicit "not included"
    // onto a child the analyst hadn't gotten to yet, just because dependents
    // overall were included.
    if (shouldIncludeBenefitInApi(benefits.childBenefitDescriptionType)) {
      transformed.child = {
        benefitDescription: buildChildBenefitDescription(
          benefits.childBenefitDescriptionType,
          benefits.childBenefitDescriptionAmount,
          benefits.childBenefitDescriptionMinimum,
        ),
      } as CriticalIllnessChildBenefit;
    }
  } else if (dependentsAnswered) {
    transformed.child = {
      benefitDescription: { type: "notIncluded" },
    } as CriticalIllnessChildBenefit;
  }

  // ========================================
  // ILLNESS-SPECIFIC BENEFITS
  // ========================================

  transformed.invasiveCancer = transformTypeAmountToApi(
    benefits.invasiveCancerType,
    benefits.invasiveCancerAmount,
    ["percentage"],
  ) as CriticalIllnessCoverage | undefined;

  transformed.nonInvasiveCancer = transformTypeAmountToApi(
    benefits.nonInvasiveCancerType,
    benefits.nonInvasiveCancerAmount,
    ["percentage"],
  ) as CriticalIllnessCoverage | undefined;

  transformed.category1HeartAttack = transformTypeAmountToApi(
    benefits.category1HeartAttackType,
    benefits.category1HeartAttackAmount,
    ["percentage"],
  ) as CriticalIllnessCoverage | undefined;

  transformed.category1Stroke = transformTypeAmountToApi(
    benefits.category1StrokeType,
    benefits.category1StrokeAmount,
    ["percentage"],
  ) as CriticalIllnessCoverage | undefined;

  transformed.kidneyFailure = transformTypeAmountToApi(
    benefits.kidneyFailureType,
    benefits.kidneyFailureAmount,
    ["percentage"],
  ) as CriticalIllnessCoverage | undefined;

  // ========================================
  // PAYOUT AND OCCURRENCE LIMITS
  // ========================================

  transformed.maximumPayout = transformTypeAmountToApi(
    benefits.maximumPayoutType,
    benefits.maximumPayoutAmount,
    ["dollarAmount", "percentage"],
  ) as CriticalIllnessMaximumPayout | undefined;

  Object.assign(
    transformed,
    transformOccurrencesToApi(
      benefits.occurrenceOfDifferentIllnessType,
      benefits.occurrenceOfDifferentIllnessMonths,
      benefits.additionalOccurrenceOfSameIllnessType,
      benefits.additionalOccurrenceOfSameIllnessMonths,
    ),
  );

  // ========================================
  // AGE AND CONDITIONS
  // ========================================

  // Transform ageReductionSchedule from separate type and details fields
  const ageReductionSchedule = transformAgeReductionScheduleToApi(
    benefits.ageReductionScheduleType,
    benefits.ageReductionScheduleSchedules,
  ) as CriticalIllnessBenefitsApi["ageReductionSchedule"];
  if (ageReductionSchedule) {
    transformed.ageReductionSchedule = ageReductionSchedule;
  }

  // Pre-Existing Condition
  transformed.preExistingCondition = transformPreExistingConditionToApi(
    benefits.preExistingConditionType,
    benefits.preExistingConditionMonthsDetail,
    "none",
    "01/01",
  ) as unknown as CriticalIllnessPreExistingCondition | undefined;

  // ========================================
  // ADDITIONAL BENEFITS AND OPTIONS
  // ========================================

  transformed.portability =
    benefits.portability || RfpInclusionStatusEnum.notIncluded;

  transformed.wellnessBenefit = transformTypeAmountToApi(
    benefits.wellnessBenefitType,
    benefits.wellnessBenefitAmount,
    ["dollarAmount"],
  ) as CriticalIllnessWellnessBenefit | undefined;

  if (shouldIncludeBenefitInApi(benefits.annualOpenEnrollment)) {
    transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }
  transformed.ageBasis = benefits.ageBasis || RfpAgeBasisEnum.notIncluded;

  // Note: benefitClassAvailability is handled automatically as a flat field

  // Extended (non-v5) fields — CD-390/CD-2074. Sent as a full object (a
  // full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan doesn't
  // drop the extended values already on it. Omitted entirely when every
  // extended field is unset, so an edit that never touches these fields
  // doesn't create an empty extended row on the backend.
  const extended = {
    skinCancer: transformTypeAmountToApi(
      benefits.skinCancerType,
      benefits.skinCancerAmount,
      ["percentage", "dollarAmount"],
    ),
    advancedParkinsons: transformTypeAmountToApi(
      benefits.advancedParkinsonsType,
      benefits.advancedParkinsonsAmount,
      ["percentage", "dollarAmount"],
    ),
    als: transformTypeAmountToApi(benefits.alsType, benefits.alsAmount, [
      "percentage",
      "dollarAmount",
    ]),
    eligibilityHours: benefits.eligibilityHours,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return cleanupNotDisclosedBenefits(transformed) as CriticalIllnessBenefitsApi;
};

/**
 * Transforms Critical Illness benefits from API format to frontend grid format
 *
 * Converts nested API structures into flat grid fields for employee, spouse, child,
 * illness-specific coverages, payout limits, and occurrence tracking.
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object matching the grid structure (flat fields)
 */
export const transformCriticalIllnessBenefitsFromApi = (
  benefits: CriticalIllnessBenefitsApi | null | undefined,
): CriticalIllnessBenefitsGrid => {
  const transformed: Partial<CriticalIllnessBenefitsGrid> = {
    objectType: "criticalIllness",
  };

  if (!benefits) {
    return transformed as CriticalIllnessBenefitsGrid;
  }

  // ========================================
  // EMPLOYEE BENEFITS
  // ========================================

  if (benefits.employee) {
    Object.assign(
      transformed,
      transformBenefitPersonFromApi(benefits.employee, "employee"),
    );
  }

  // ========================================
  // DEPENDENT BENEFITS INCLUDED (FE only)
  // ========================================

  const dependentBenefitsIncluded = deriveDependentBenefitsIncluded(benefits);
  if (dependentBenefitsIncluded) {
    transformed.dependentBenefitsIncluded = dependentBenefitsIncluded;
  }

  // ========================================
  // SPOUSE BENEFITS
  // ========================================

  if (benefits.spouse) {
    Object.assign(
      transformed,
      transformBenefitPersonFromApi(benefits.spouse, "spouse"),
    );
  }

  // ========================================
  // CHILD BENEFITS
  // ========================================

  Object.assign(transformed, transformChildBenefitsFromApi(benefits.child));

  // ========================================
  // ILLNESS-SPECIFIC BENEFITS
  // ========================================

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.invasiveCancer, "invasiveCancer"),
  );

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.nonInvasiveCancer, "nonInvasiveCancer"),
  );

  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.category1HeartAttack,
      "category1HeartAttack",
    ),
  );

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.category1Stroke, "category1Stroke"),
  );

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.kidneyFailure, "kidneyFailure"),
  );

  // ========================================
  // PAYOUT AND OCCURRENCE LIMITS
  // ========================================

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.maximumPayout, "maximumPayout"),
  );

  Object.assign(
    transformed,
    transformOccurrenceFromApi(
      benefits.occurrenceOfDifferentIllness,
      "occurrenceOfDifferentIllness",
    ),
  );

  Object.assign(
    transformed,
    transformOccurrenceFromApi(
      benefits.additionalOccurrenceOfSameIllness,
      "additionalOccurrenceOfSameIllness",
    ),
  );

  // ========================================
  // AGE AND CONDITIONS
  // ========================================

  // Transform ageReductionSchedule from API object to grid fields
  Object.assign(
    transformed,
    transformAgeReductionScheduleFromApi(benefits.ageReductionSchedule),
  );

  // Pre-Existing Condition
  Object.assign(
    transformed,
    transformPreExistingConditionFromApi(benefits.preExistingCondition),
  );

  // ========================================
  // ADDITIONAL BENEFITS AND OPTIONS
  // ========================================

  transformed.portability = benefits.portability;

  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.wellnessBenefit, "wellnessBenefit"),
  );

  transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  transformed.ageBasis = benefits.ageBasis;

  // Note: benefitClassAvailability is handled automatically as a flat field

  // Extended (non-v5) fields — CD-390/CD-2074
  // skinCancer/advancedParkinsons/als are type+amount pairs (CD-2325).
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.extended?.skinCancer, "skinCancer"),
  );
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      benefits.extended?.advancedParkinsons,
      "advancedParkinsons",
    ),
  );
  Object.assign(
    transformed,
    transformTypeAmountFromApi(benefits.extended?.als, "als"),
  );
  transformed.eligibilityHours =
    benefits.extended?.eligibilityHours ?? undefined;

  return transformed as CriticalIllnessBenefitsGrid;
};

/**
 * Grid keys that persist under `benefits.extended` — see
 * STD_EXTENDED_GRID_FIELD_KEYS.
 *
 * skinCancerAmount/advancedParkinsonsAmount/alsAmount are deliberately
 * absent: they're `nested: true` siblings of their *Type row, never
 * independently editable on the grid (the popup sets both together, keyed
 * off the Type row) — only the Type key needs to be named here.
 */
export const CRITICAL_ILLNESS_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> =
  new Set([
    "skinCancerType",
    "advancedParkinsonsType",
    "alsType",
    "eligibilityHours",
  ]);
